/**
 * CR-8227: Platform Config's Organisation section once the customer's
 * structure exists — the recorded structure and level names, a way to record
 * the structure on an instance set up before this section existed, Rename
 * levels, and the customer tree with the Helix team's full controls.
 */
import { useState } from 'react';
import { platformSettingsAPI } from '../../../lib/api';
import type { OrganisationStructure } from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/api/types/common';
import { Button } from '../../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { OrgChartView } from '../../orgchart';
import { OrganisationLevelNames } from './OrganisationLevelNames';
import {
  SHAPES,
  levelsFor,
  structureTitle,
  type LevelNames,
  type OrganisationShape,
} from './organisationSetup';

interface OrganisationSummaryProps {
  structure: OrganisationStructure;
  labels: LevelNames;
  /** Lets the page pick up a saved structure or level names. */
  onSaved: (structure: OrganisationStructure, labels: LevelNames) => void;
}

/**
 * @param structure - the recorded structure, '' when none is recorded
 * @param labels - the instance's level names
 * @param onSaved - called after a successful save
 */
export function OrganisationSummary({ structure, labels, onSaved }: OrganisationSummaryProps) {
  const [chosen, setChosen] = useState<OrganisationShape | ''>('');
  const [renaming, setRenaming] = useState(false);
  const [draftLabels, setDraftLabels] = useState<LevelNames>(labels);
  const [saving, setSaving] = useState(false);
  // Remounts the tree after a rename so its own header picks up the new names.
  const [treeKey, setTreeKey] = useState(0);

  const levels = levelsFor(structure);
  const shown: Array<[string, string]> = [
    ['Structure', structureTitle(structure)],
    ...(levels.group ? [['Top level name', labels.group] as [string, string]] : []),
    ...(levels.division ? [['Middle level name', labels.division] as [string, string]] : []),
    ['Site level name', labels.site],
  ];

  const save = async (nextStructure: OrganisationStructure, nextLabels: LevelNames, done: string) => {
    setSaving(true);
    try {
      await platformSettingsAPI.patchSettings({
        organisation_structure: nextStructure,
        hierarchy_level_1_label: nextLabels.group.trim(),
        hierarchy_level_2_label: nextLabels.division.trim(),
        hierarchy_level_3_label: nextLabels.site.trim(),
      });
      onSaved(nextStructure, {
        group: nextLabels.group.trim(),
        division: nextLabels.division.trim(),
        site: nextLabels.site.trim(),
      });
      setTreeKey((key) => key + 1);
      toast.success(done);
      return true;
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Could not save. Please try again.'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveLabels = async () => {
    const used = levelsFor(structure);
    if ((used.group && !draftLabels.group.trim()) || (used.division && !draftLabels.division.trim()) || !draftLabels.site.trim()) {
      toast.error('Every level needs a name.');
      return;
    }
    if (await save(structure, draftLabels, 'Level names saved')) setRenaming(false);
  };

  const recordStructure = async () => {
    if (!chosen) return;
    if (await save(chosen, labels, 'Structure recorded')) setChosen('');
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
        {shown.map(([title, value]) => (
          <div key={title} className="space-y-1">
            <div className="text-muted-foreground">{title}</div>
            <div className="font-semibold">{value}</div>
          </div>
        ))}
      </div>

      {structure === '' ? (
        <div className="space-y-2 rounded-md border p-4">
          <p className="text-sm">
            This instance was set up before its structure was recorded. Record the shape the tree below
            already has; it changes nothing the customer sees.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Select value={chosen} onValueChange={(value) => setChosen(value as OrganisationShape)}>
              <SelectTrigger className="w-72" aria-label="Structure">
                <SelectValue placeholder="Choose the structure" />
              </SelectTrigger>
              <SelectContent>
                {SHAPES.map((shape) => (
                  <SelectItem key={shape.id} value={shape.id}>{shape.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button disabled={!chosen || saving} onClick={recordStructure}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Record structure
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          Changing the structure type after go live is not available yet.
        </p>
      )}

      {renaming ? (
        <div className="space-y-3 rounded-md border p-4">
          <OrganisationLevelNames structure={structure} labels={draftLabels} onChange={setDraftLabels} disabled={saving} />
          <div className="flex gap-2">
            <Button disabled={saving} onClick={saveLabels}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save level names
            </Button>
            <Button variant="outline" disabled={saving} onClick={() => setRenaming(false)}>Cancel</Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" onClick={() => { setDraftLabels(labels); setRenaming(true); }}>
          Rename levels
        </Button>
      )}

      <div className="space-y-2">
        <OrgChartView key={treeKey} companyType="customer" />
        <p className="text-sm text-muted-foreground">
          The same tree as the customer's Organisation tab. Only the Helix team can add, edit, move or
          delete here; customer users see it read only. Renaming a {labels.site.toLowerCase()} asks for
          confirmation, because its name links it to its imported data.
        </p>
      </div>
    </div>
  );
}
